import { createContext, ReactNode, useContext, useEffect, useState, useRef } from "react";
import {
  useQuery,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { insertUserSchema, User as SelectUser, InsertUser } from "@shared/schema";
import { getQueryFn, apiRequest, queryClient } from "../lib/queryClient";
import { useToast } from "@/hooks/use-toast";

type AuthContextType = {
  user: SelectUser | null;
  isLoading: boolean;
  error: Error | null;
  loginMutation: UseMutationResult<SelectUser, Error, LoginData>;
  logoutMutation: UseMutationResult<void, Error, void>;
  registerMutation: UseMutationResult<SelectUser, Error, InsertUser>;
};

type LoginData = Pick<InsertUser, "username" | "password">;

export const AuthContext = createContext<AuthContextType | null>(null);
export function AuthProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  const ssoExchangeAttempted = useRef(false);
  const [ssoExchanging, setSsoExchanging] = useState(false);

  // ── SSO Token Exchange ─────────────────────────────────────────────
  // When the Enterprise Suite redirects here with ?token=<JWT>,
  // exchange it for a session cookie before doing anything else.
  useEffect(() => {
    if (ssoExchangeAttempted.current) return;
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get('token');
    if (!ssoToken) return;

    ssoExchangeAttempted.current = true;
    setSsoExchanging(true);

    // Clean the token from the URL immediately (security: don't leave JWT in address bar)
    const cleanUrl = window.location.pathname + window.location.hash;
    window.history.replaceState({}, '', cleanUrl);

    apiRequest("POST", "/api/auth/sso-exchange", { token: ssoToken })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.message || 'SSO exchange failed');
        }
        const user = await res.json();
        queryClient.setQueryData(["/api/user"], user);
        queryClient.invalidateQueries();
      })
      .catch((err) => {
        console.error('[SSO] Token exchange failed:', err);
        toast({
          title: "SSO Login Failed",
          description: err.message || "Could not sign in via Enterprise Suite. Please try again.",
          variant: "destructive",
        });
      })
      .finally(() => {
        setSsoExchanging(false);
      });
  }, [toast]);

  const {
    data: user,
    error,
    isLoading,
  } = useQuery<SelectUser | undefined, Error>({
    queryKey: ["/api/user"],
    queryFn: getQueryFn({ on401: "returnNull" }),
  });

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginData) => {
      const res = await apiRequest("POST", "/api/login", credentials);
      return await res.json();
    },
    onSuccess: (user: SelectUser) => {
      queryClient.setQueryData(["/api/user"], user);
      queryClient.invalidateQueries();
      toast({
        title: "Login successful",
        description: "Login successful",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Login failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (credentials: InsertUser) => {
      const res = await apiRequest("POST", "/api/register", credentials);
      return await res.json();
    },
    onSuccess: (user: SelectUser) => {
      queryClient.setQueryData(["/api/user"], user);
      queryClient.invalidateQueries();
      toast({
        title: "Registration successful",
        description: "Account created successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Registration failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/logout");
    },
    onSuccess: () => {
      queryClient.setQueryData(["/api/user"], null);
      toast({
        title: "Logged out",
        description: "You have been successfully logged out.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Logout failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return (
    <AuthContext.Provider
      value={{
        user: user ?? null,
        isLoading: isLoading || ssoExchanging,
        error,
        loginMutation,
        logoutMutation,
        registerMutation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
